import React, { useEffect, useState } from "react";
import { Avatar, Box, Chip, CircularProgress, Tooltip, Typography } from "@mui/material";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import ActionItemService from "../../services/actionItems/ActionItemService";
import { formatClientDomain, getAvatarColor, getAvatarInitials } from "../../utils/utils";

interface ActionItemHistoryProps {
  actionItemId?: string;
}

const ActionItemHistory: React.FC<ActionItemHistoryProps> = ({ actionItemId }) => {
  const { showSnackbar } = useDisplaySnackbar();
  const [actionItemHistory, setActionItemHistory] = useState<any[]>([]);

  const { 
    isLoading: isHistoryLoading, 
    isFetching: isHistoryFetching, 
    isSuccess: isHistorySuccess, 
    isError: isHistoryError, 
    error: historyError, 
    data: historyData 
  } = ActionItemService.useGetActionItemHistory({ actionItemId: actionItemId || "" }, !!actionItemId);

  useEffect(() => {
    if (isHistorySuccess && historyData) {
      const updatedHistory = historyData.data.map((item: any) => {
        const changes = [...item.changes];

        // Move 'task_created' to the top if it exists
        const taskCreatedIndex = changes.findIndex(c => c.fieldName === "task_created");
        if (taskCreatedIndex > 0) {
          const [taskCreated] = changes.splice(taskCreatedIndex, 1);
          changes.unshift(taskCreated);
        }

        return {
          ...item,
          changes,
        };
      });

      setActionItemHistory(updatedHistory);
    } else if (isHistoryError) {
      showSnackbar("error", historyError?.message || "Failed to load data.");
    }
  }, [isHistorySuccess, historyData, isHistoryError, historyError]);

  const formatStatus = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      to_do: { label: "To Do", color: "#ff9800" },
      in_progress: { label: "In Progress", color: "#1976d2" },
      done: { label: "Done", color: "#4caf50" },
      pr_review: { label: "PR Review", color: "#875B14"},
      product_testing: { label: "Product Testing", color: "#195678"},
    };
    const formatted = statusMap[status];
    if (!formatted)
      return (
        <Typography variant="body2" sx={{ color: "#6B7280" }}>
          {status || "Unassigned"}
        </Typography>
      );
    return (
      <Box
        sx={{
          display: "inline-block",
          px: 1.2,
          py: 0.4,
          borderRadius: "4px",
          fontSize: "12px",
          fontWeight: 600,
          border: `1px solid ${formatted.color}`,
        }}>
        {formatted.label}
      </Box>
    );
  };

  const SubscriberAvatar = ({ item }: any) => {
    const isProfile = item?.type === "PROFILE";
    return (
      <Avatar
        src={item?.pictureUrl || undefined}
        alt={item?.name}
        sx={{
          width: isProfile ? "22px" : "20px",
          height: isProfile ? "22px" : "20px",
          fontSize: "12px",
          borderRadius: isProfile ? "50%" : "4px",
          bgcolor: getAvatarColor(item?.name),
        }}
      >
        {!item?.pictureUrl && getAvatarInitials(item?.name)}
      </Avatar>
    );
  };

  const groupByType = (arr: any[]) => {
    return arr.reduce(
      (acc, item) => {
        acc[item.type]?.push(item);
        return acc;
      },
      { PROFILE: [], USER_GROUP: [] }
    );
  };

  const renderValue = (change: any, value: any) => {
    if (!value || value === "") return "Unassigned";

    if (change.fieldName === "attachments") {
        try {
        const attachments = Array.isArray(value)
            ? value
            : typeof value === "string"
            ? JSON.parse(value)
            : [];

        if (!attachments.length) return "No attachments";

        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {attachments.map((att: any, idx: number) => (
                <a
                key={idx}
                href={att.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    color: "#1976d2",
                    textDecoration: "none",
                    fontSize: "13px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                }}
                >
                {att.description || "Untitled"}
                </a>
            ))}
            </Box>
        );
        } catch {
        return "Invalid attachments format";
        }
    }

    if (change.fieldName === "client") {
      if (typeof value === "object" && value.domain) return formatClientDomain(value.domain);
      return "Unassigned";
    }

    if (change.fieldName === "due_date") {
      const date = new Date(value);
      if (isNaN(date.getTime())) return "Unassigned";
      return date.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
    }

    if (["assignee", "coach", "sponsor"].includes(change.fieldName)) {
      if (typeof value !== "object" || ((!value.name || value.name.trim() === "") && (!value.pictureUrl || value.pictureUrl.trim() === ""))) {
        return "Unassigned";
      }
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          <Avatar
            src={value.pictureUrl}
            alt={value.name}
            sx={{
              width: 24,
              height: 24,
              display: "inline-flex",
              verticalAlign: "middle",
            }}
          />
          <Typography variant="body2" sx={{ fontSize: "13px" }}>
            {value.name}
          </Typography>
        </Box>
      );
    }

    if (change.fieldName === "description") {
      if (!value) return "Unassigned";

      return (
        <Box
          sx={{
            fontSize: "13px",
            color: "#4B5563",
            maxWidth: "100%",
            "& p": { margin: 0, padding: 0 },
            "& ul": { 
              margin: "4px 0 0 0",
              paddingLeft: "20px", 
              listStyleType: "disc",
            },
            "& ol": {
              margin: "4px 0 0 0",
              paddingLeft: "20px",
              listStyleType: "decimal",
            },
            "& li": { 
              marginBottom: "6px",
              display: "list-item",
            },
          }}
          dangerouslySetInnerHTML={{ __html: value }}
        />
      );
    }

    if (change.fieldName === "priority") {
      if (!value) return "Unassigned";
      const formatted =
        typeof value === "string"
          ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
          : value;
      return (
        <Typography variant="body2" sx={{ fontSize: "13px", color: "#4B5563" }}>
          {formatted}
        </Typography>
      );
    }
    if (change.fieldName === "tags" || change.fieldName === "tag") {
      const renderTags = (tagString: string) => {
        const tags = tagString.split(",").map((t) => t.trim()).filter(Boolean);
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {tags.map((tag, idx) => (
              <Chip
                  key={idx}
                  label={tag}
                  size="small"
                  className="history-tags-chip"
                />
            ))}
          </Box>
        );
      };

      return renderTags(value);
    }
    if (change.fieldName === "subscribers") {
      if (!Array.isArray(value) || value.length === 0) return "Unassigned";

      const maxVisible = 4;
      const visible = value.slice(0, maxVisible);
      const remainingCount = value.length - maxVisible;
      const grouped = groupByType(value);

      return (
        <Tooltip
          placement="right-start"
          componentsProps={{
            tooltip: {
              className: "custom-tooltip",
            },
          }}
          title={
            <Box sx={{ display: "flex", flexDirection: "column", p: 1, gap: 1 }}>
              {grouped.USER_GROUP.length > 0 && (
                <>
                  <Typography className="group-by-text">
                    Spaces
                  </Typography>
                  {grouped.USER_GROUP.map((user: any, idx: number) => (
                    <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <SubscriberAvatar item={user} size={20} />
                      <Typography sx={{ fontSize: "13px" }}>{user?.name}</Typography>
                    </Box>
                  ))}
                </>
              )}

              {grouped.PROFILE.length > 0 && (
                <>
                  <Typography className="group-by-text">
                    People
                  </Typography>

                  {grouped.PROFILE.map((user: any, idx: number) => (
                    <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <SubscriberAvatar item={user} size={22} />
                      <Typography sx={{ fontSize: "13px" }}>{user?.name}</Typography>
                    </Box>
                  ))}
                </>
              )}
            </Box>
          }
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}>
            {visible.map((user: any, idx: number) => (
              <SubscriberAvatar key={idx} item={user} />
            ))}

            {remainingCount > 0 && (
              <Typography sx={{ fontSize: 12, color: "#5F5F70" }}>
                +{remainingCount}
              </Typography>
            )}
          </Box>
        </Tooltip>
      );
    }

    if (change.fieldName === "status") return formatStatus(value);
    if (typeof value === "object" && value.name) return value.name;
    return typeof value === "object" ? JSON.stringify(value) : value;
  };

  const fieldDisplayMap: Record<string, string> = {
    client: "Account",
    coach: "Solo Coach",
    assignee: "Solo Player",
    sponsor: "Exec Sponsor",
    due_date: "Due Date",
    task_created: "Task"
  };

  if (isHistoryLoading || isHistoryFetching) return <CircularProgress />;

  if (!actionItemHistory?.length)
    return (
      <Typography variant="body2" sx={{ paddingLeft: "20px", fontSize: "12px" }}>
        No history available.
      </Typography>
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {actionItemHistory[0].changes.map((change: any, index: number) => {
        const displayFieldName = fieldDisplayMap[change.fieldName] || change.fieldName.charAt(0).toUpperCase() + change.fieldName.slice(1);
        const oldVal = renderValue(change, change.oldValue);
        const newVal = renderValue(change, change.newValue);

        const getActionType = () => {
            if (change.fieldName === "description") return "updated";
            if (change.fieldName === "task_created") return "created";

            const isOldUnassigned =
            oldVal === "Unassigned" || oldVal === "" || !oldVal;
            const isNewAssigned = newVal && newVal !== "Unassigned";
            if (isOldUnassigned && isNewAssigned) return "added";

            return "changed";
        };

        const actionType = getActionType();

        return (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              pb: 1.5,
              pl: 2,
            }}>
            {/* Updater avatar */}
            <Tooltip title={change.updatedBy.name} arrow>
              <Avatar
                src={change.updatedBy.pictureUrl}
                alt={change.updatedBy.name}
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: "14px",
                }}>
                {!change.updatedBy.pictureUrl && change.updatedBy.name?.charAt(0)?.toUpperCase()}
              </Avatar>
            </Tooltip>

            {/* Change details */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 0.5,
                }}
                >
                <Typography
                    component="span"
                    sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    }}
                >
                    {change.updatedBy.name}
                </Typography>
                <Typography
                    component="span"
                    sx={{
                    fontWeight: 400,
                    fontSize: "14px",
                    }}
                >
                    {actionType} the
                </Typography>
                <Typography
                    component="span"
                    sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    }}
                >
                    {displayFieldName}
                </Typography>
            </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: "#9CA3AF",
                  display: "block",
                }}>
                {new Date(change.createdAt).toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                  year: "numeric",
                })}
              </Typography>
              {actionType !== "created" && (
              <Typography
                variant="body2"
                sx={{
                  color: "#4B5563",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}>
                {renderValue(change, change.oldValue)} ➔ {renderValue(change, change.newValue)}
              </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ActionItemHistory;
